import React, { useState, useCallback } from "react";
import { db } from "../firebase/firebaseConfig"; // ✅ Fixed import path
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { Helmet } from "react-helmet-async";

/* ─── Design tokens ───────────────────────────────────────── */
const T = {
  rose:      "#c0506e",
  roseDark:  "#8f2d46",
  roseLight: "#e8839a",
  roseSoft:  "#fdf0f3",
  roseMid:   "#f3c5cf",
  ivory:     "#faf8f5",
  ivoryDark: "#f0ece6",
  warm100:   "#e8e3dc",
  warm200:   "#d4cfc8",
  warm400:   "#9e9189",
  warm700:   "#3d3530",
  ink:       "#1a1510",
  ffDisplay: "'Cormorant Garamond', Georgia, serif",
  ffBody:    "'Outfit', system-ui, sans-serif",
};

/* ─── Validation ──────────────────────────────────────────── */
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

/* ─── Field component ─────────────────────────────────────── */
function Field({ label, error, multiline = false, rows = 4, ...props }) {
  const [focused, setFocused] = useState(false);
  const sharedStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "0.75rem 1rem",
    fontFamily: T.ffBody,
    fontSize: 14,
    color: T.ink,
    background: focused ? "#fff" : T.ivory,
    border: `1.5px solid ${error ? T.rose : focused ? T.rose : T.warm200}`,
    borderRadius: 10,
    outline: "none",
    transition: "border-color 200ms, background 200ms, box-shadow 200ms",
    boxShadow: focused ? `0 0 0 3px rgba(192,80,110,.12)` : "none",
  };

  return (
    <div style={{ marginBottom: "1.1rem" }}>
      <label style={{
        display: "block",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: error ? T.rose : focused ? T.rose : T.warm400,
        marginBottom: 6,
        fontFamily: T.ffBody,
        transition: "color 180ms",
      }}>{label}</label>

      {multiline ? (
        <textarea
          {...props}
          rows={rows}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{ ...sharedStyle, resize: "vertical", lineHeight: 1.6, minHeight: `${rows * 1.6}rem` }}
        />
      ) : (
        <input
          {...props}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={sharedStyle}
        />
      )}

      {error && (
        <p style={{ margin: "5px 0 0 2px", fontSize: 12, color: T.rose, fontFamily: T.ffBody }}>
          {error}
        </p>
      )}
    </div>
  );
}

/* ─── Status alert ────────────────────────────────────────── */
function StatusAlert({ status, onClose }) {
  if (!status) return null;
  const isError = status.type === "error";
  return (
    <div style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "0.9rem 1rem",
      borderRadius: 10,
      border: `1px solid ${isError ? T.roseMid : "#b7e3c8"}`,
      background: isError ? T.roseSoft : "#f0faf5",
      marginBottom: "1.25rem",
      animation: "fadeSlide 300ms cubic-bezier(0.16,1,0.3,1) both",
    }}>
      <span style={{ fontSize: 15, flexShrink: 0, color: isError ? T.rose : "#15803d" }}>
        {isError ? "⚠" : "✓"}
      </span>
      <p style={{ margin: 0, flex: 1, fontSize: 13, fontFamily: T.ffBody, color: isError ? T.roseDark : "#1a5c35", lineHeight: 1.5 }}>
        {status.message}
      </p>
      <button
        onClick={onClose}
        aria-label="Dismiss"
        style={{ background: "none", border: "none", cursor: "pointer", color: T.warm400, fontSize: 16, padding: 0, flexShrink: 0 }}
      >✕</button>
    </div>
  );
}

/* ─── Contact info card ───────────────────────────────────── */
function InfoCard({ icon, label, value, href }) {
  return (
    <a
      href={href}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "0.9rem 1rem",
        background: "#fff",
        border: `1px solid ${T.warm100}`,
        borderRadius: 12,
        textDecoration: "none",
        transition: "border-color 180ms, box-shadow 180ms",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = T.roseMid;
        e.currentTarget.style.boxShadow = `0 4px 16px rgba(192,80,110,.08)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = T.warm100;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: T.roseSoft, border: `1px solid ${T.roseMid}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 16, flexShrink: 0,
      }}>{icon}</div>
      <div>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: T.warm400, fontFamily: T.ffBody }}>{label}</p>
        <p style={{ margin: "2px 0 0", fontSize: 14, fontWeight: 500, color: T.ink, fontFamily: T.ffBody }}>{value}</p>
      </div>
    </a>
  );
}

/* ─── Main component ──────────────────────────────────────── */
const ContactForm = () => {
  const [form, setForm]     = useState({ name: "", email: "", subject: "", message: "" });
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus]   = useState(null);

  const set  = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const blur = (key) => () => setTouched((p) => ({ ...p, [key]: true }));

  const errors = {
    name:    touched.name    && !form.name.trim()        ? "Your name is required" : null,
    email:   touched.email   && !isValidEmail(form.email) ? "Enter a valid email address" : null,
    message: touched.message && form.message.trim().length < 10 ? "Message must be at least 10 characters" : null,
  };

  const canSubmit = form.name.trim() && isValidEmail(form.email) && form.message.trim().length >= 10 && !loading;

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    if (!canSubmit) return;

    setLoading(true);
    setStatus(null);

    try {
      await addDoc(collection(db, "messages"), {
        name:      form.name.trim(),
        email:     form.email.trim(),
        subject:   form.subject.trim(),
        message:   form.message.trim(),
        timestamp: serverTimestamp(),
      });
      setForm({ name: "", email: "", subject: "", message: "" });
      setTouched({});
      setStatus({ type: "success", message: "Message sent! We'll get back to you within 24 hours." });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Contact form error:", err);
      setStatus({ type: "error", message: "Failed to send message. Please try again." });
    } finally {
      setLoading(false);
    }
  }, [form, canSubmit]);

  return (
    <>
      <Helmet>
        <title>Contact Us | Glossy Lux Hair</title>
        <meta name="description" content="Get in touch with Glossy Lux Hair. We're based in Winneba, Ghana. Questions about orders, wholesale enquiries or anything else — we're happy to help." />
        <link rel="canonical" href="https://glossyluxhair.com/contact" />
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Outfit:wght@400;500;600&display=swap');
        @keyframes fadeUp   { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeSlide{ from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin     { to { transform: rotate(360deg); } }
        .contact-card { animation: fadeUp 480ms cubic-bezier(0.16,1,0.3,1) both; }
        .contact-submit:not(:disabled):hover {
          background: ${T.roseDark} !important;
          box-shadow: 0 8px 28px rgba(192,80,110,.32) !important;
          transform: translateY(-2px);
        }
        .contact-submit { transition: background 220ms, box-shadow 220ms, transform 200ms; }
        .contact-submit:disabled { opacity: .5; cursor: not-allowed; }

        .contact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(1.5rem, 4vw, 3rem);
          align-items: start;
        }
        @media (max-width: 768px) {
          .contact-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse 70% 50% at 100% 0%, rgba(192,80,110,.06) 0%, transparent 65%),
          radial-gradient(ellipse 50% 40% at 0% 100%, rgba(192,80,110,.04) 0%, transparent 55%),
          ${T.ivoryDark}
        `,
        fontFamily: T.ffBody,
        padding: "clamp(2rem, 6vw, 4.5rem) clamp(1rem, 5vw, 2rem)",
      }}>

        {/* Page title */}
        <div style={{ maxWidth: 1100, margin: "0 auto clamp(2rem, 5vw, 3rem)" }}>
          <p style={{ margin: "0 0 .4rem", fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: T.rose }}>
            GlossyLuxHair
          </p>
          <h1 style={{ margin: "0 0 .5rem", fontFamily: T.ffDisplay, fontSize: "clamp(2rem, 5vw, 2.75rem)", fontWeight: 700, color: T.ink, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
            Get in Touch
          </h1>
          <p style={{ margin: 0, fontSize: 15, color: T.warm400, lineHeight: 1.6 }}>
            Questions, wholesale enquiries, or just want to say hello?
          </p>
        </div>

        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <StatusAlert status={status} onClose={() => setStatus(null)} />

          <div className="contact-grid">

            {/* ── Left — contact info ── */}
            <div className="contact-card" style={{ animationDelay: "80ms" }}>
              <div style={{
                background: "#fff",
                borderRadius: 16,
                border: `1px solid ${T.warm100}`,
                boxShadow: `0 2px 8px rgba(26,21,16,.04), 0 12px 40px rgba(192,80,110,.05)`,
                overflow: "hidden",
                marginBottom: "1.5rem",
              }}>
                <div style={{ height: 3, background: `linear-gradient(90deg, transparent 0%, ${T.roseMid} 15%, ${T.rose} 50%, ${T.roseMid} 85%, transparent 100%)` }} />
                <div style={{ padding: "clamp(1.5rem, 4vw, 2rem)" }}>
                  <h2 style={{ margin: "0 0 .3rem", fontFamily: T.ffDisplay, fontSize: "1.4rem", fontWeight: 700, color: T.roseDark }}>
                    Contact Information
                  </h2>
                  <p style={{ margin: "0 0 1.5rem", fontSize: 13, color: T.warm400 }}>
                    We're based in Winneba, Ghana and typically respond within 24 hours.
                  </p>

                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <InfoCard
                      icon="📍"
                      label="Location"
                      value="North Campus, Winneba, Ghana"
                      href="https://maps.google.com/?q=Winneba+Ghana"
                    />
                    <InfoCard
                      icon="📞"
                      label="Phone"
                      value="0530 557 187"
                      href="tel:+233530557187"
                    />
                    <InfoCard
                      icon="📸"
                      label="Instagram"
                      value="@glossy_lux_hair"
                      href="https://instagram.com/glossy_lux_hair"
                    />
                    <InfoCard
                      icon="📘"
                      label="Facebook"
                      value="glossy_lux_hair"
                      href="https://facebook.com/glossy_lux_hair"
                    />
                  </div>
                </div>
              </div>

              {/* Hours card */}
              <div style={{
                background: `linear-gradient(135deg, ${T.roseSoft} 0%, #fde8ed 100%)`,
                border: `1px solid ${T.roseMid}`,
                borderRadius: 16,
                padding: "1.25rem 1.5rem",
              }}>
                <p style={{ margin: "0 0 .75rem", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: T.rose, fontFamily: T.ffBody }}>
                  Response Hours
                </p>
                {[
                  ["Monday – Friday", "8:00 AM – 6:00 PM"],
                  ["Saturday",        "9:00 AM – 4:00 PM"],
                  ["Sunday",          "Closed"],
                ].map(([day, time]) => (
                  <div key={day} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${T.roseMid}` }}>
                    <span style={{ fontFamily: T.ffBody, fontSize: 13, color: T.warm700 }}>{day}</span>
                    <span style={{ fontFamily: T.ffBody, fontSize: 13, fontWeight: 600, color: T.roseDark }}>{time}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Right — form ── */}
            <div className="contact-card">
              <div style={{
                background: "#fff",
                borderRadius: 16,
                border: `1px solid ${T.warm100}`,
                boxShadow: `0 2px 8px rgba(26,21,16,.04), 0 12px 40px rgba(192,80,110,.05)`,
                overflow: "hidden",
              }}>
                <div style={{ height: 3, background: `linear-gradient(90deg, transparent 0%, ${T.roseMid} 15%, ${T.rose} 50%, ${T.roseMid} 85%, transparent 100%)` }} />
                <div style={{ padding: "clamp(1.5rem, 4vw, 2rem)" }}>
                  <h2 style={{ margin: "0 0 .3rem", fontFamily: T.ffDisplay, fontSize: "1.4rem", fontWeight: 700, color: T.roseDark }}>
                    Send a Message
                  </h2>
                  <p style={{ margin: "0 0 1.5rem", fontSize: 13, color: T.warm400 }}>
                    All fields marked * are required
                  </p>

                  <form onSubmit={handleSubmit} noValidate>
                    {/* Name + Email row */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                      <Field
                        label="Full Name *"
                        type="text"
                        placeholder="Jane Doe"
                        value={form.name}
                        error={errors.name}
                        onChange={set("name")}
                        onBlur={blur("name")}
                        autoComplete="name"
                      />
                      <Field
                        label="Email *"
                        type="email"
                        placeholder="you@example.com"
                        value={form.email}
                        error={errors.email}
                        onChange={set("email")}
                        onBlur={blur("email")}
                        autoComplete="email"
                      />
                    </div>

                    <Field
                      label="Subject"
                      type="text"
                      placeholder="Order enquiry, wholesale, general question…"
                      value={form.subject}
                      onChange={set("subject")}
                    />

                    <Field
                      label="Message *"
                      multiline
                      rows={5}
                      placeholder="How can we help you?"
                      value={form.message}
                      error={errors.message}
                      onChange={set("message")}
                      onBlur={blur("message")}
                    />

                    {/* Security note */}
                    <p style={{ fontSize: 12, color: T.warm400, display: "flex", alignItems: "center", gap: 6, margin: "0 0 1rem" }}>
                      <span>🔒</span> Your information is kept private and never shared.
                    </p>

                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className="contact-submit"
                      style={{
                        width: "100%",
                        padding: "1rem 1.5rem",
                        fontFamily: T.ffBody,
                        fontWeight: 600,
                        fontSize: 15,
                        letterSpacing: "0.04em",
                        color: "#fff",
                        background: canSubmit ? T.rose : T.warm200,
                        border: "none",
                        borderRadius: 12,
                        cursor: canSubmit ? "pointer" : "not-allowed",
                        boxShadow: canSubmit ? `0 4px 20px rgba(192,80,110,.22)` : "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      {loading ? (
                        <>
                          <span style={{
                            width: 16, height: 16,
                            border: "2px solid rgba(255,255,255,.4)",
                            borderTopColor: "#fff",
                            borderRadius: "50%",
                            display: "inline-block",
                            animation: "spin .7s linear infinite",
                          }} />
                          Sending…
                        </>
                      ) : (
                        <><span>✦</span> Send Message</>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ContactForm;